if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');
            const productUrl = opener.getAttribute('data-product-url');

            this.preprocessHTML(productElement, productUrl);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            this.mountProductShareInline(this.modalContent.querySelector('product-info'));
            this.applyVariantLockContext(this.modalContent.querySelector('product-info'), productUrl);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement, productUrl) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
        this.applyVariantLockContext(productElement, productUrl);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      mountProductShareInline(productInfo) {
        if (!productInfo) return;

        const titleShareTarget = productInfo.querySelector('[data-product-title-share-target]');
        const shareBlock = productInfo.querySelector('[data-product-share-inline]');
        if (titleShareTarget && shareBlock) {
          titleShareTarget.replaceChildren(shareBlock);
        }
      }

      applyVariantLockContext(productElement, productUrl) {
        if (!productElement || !productUrl) {
          return;
        }

        let search;
        try {
          search = new URL(productUrl, window.location.origin).search;
        } catch (error) {
          return;
        }

        const params = new URLSearchParams(search);
        const allowedVariants = params.get('allowed_variants');
        const hasRestrictedVariants =
          allowedVariants?.includes(',') &&
          (typeof isVariantOptionsLockedInUrl === 'function' ? isVariantOptionsLockedInUrl(search) : params.has('locked'));

        if (allowedVariants) {
          productElement.dataset.allowedVariantIds = allowedVariants;
        }

        if (!hasRestrictedVariants && typeof isVariantOptionsLockedInUrl === 'function' && !isVariantOptionsLockedInUrl(search)) {
          return;
        }

        productElement.dataset.variantLockSearch = search;

        const variantSelects = productElement.querySelector('variant-selects');
        if (!variantSelects) {
          return;
        }

        if (hasRestrictedVariants) {
          variantSelects.dataset.variantRestricted = 'true';
          if (typeof variantSelects.setRestrictedLock === 'function') {
            variantSelects.setRestrictedLock(true);
          }
          return;
        }

        variantSelects.dataset.variantLocked = 'true';
        if (typeof variantSelects.setLocked === 'function') {
          variantSelects.setLocked(true);
          return;
        }

        variantSelects.classList.add('variant-selects--locked');
        variantSelects.querySelector('[data-variant-lock-notice]')?.classList.remove('hidden');
        variantSelects.querySelector('[data-variant-lock-full]')?.classList.remove('hidden');
        variantSelects.querySelector('[data-variant-lock-restricted]')?.classList.add('hidden');
        variantSelects.querySelectorAll('.product-form__input').forEach((group) => {
          if (group.tagName === 'FIELDSET') {
            group.disabled = true;
            return;
          }

          const select = group.querySelector('select');
          if (select) select.disabled = true;
        });
      }

      removeDOMElements(productElement) {
        this.removeComplementaryAccessoryUI(productElement);

        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      removeComplementaryAccessoryUI(productElement) {
        productElement.querySelectorAll('[data-product-complementary-variants]').forEach((element) => element.remove());
        productElement.querySelectorAll('[data-product-complementary-products]').forEach((element) => element.remove());
        productElement.querySelectorAll('[data-product-complementary-stack]').forEach((element) => element.remove());
        productElement.querySelectorAll('[data-complementary-variant-modal]').forEach((element) => element.remove());
        productElement.querySelectorAll('template[data-complementary-variant-template]').forEach((element) => element.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
